import React, { useState, useEffect } from 'react';
import { getMyOrders } from '../services/api';
import { useNotification } from '../context/NotificationContext';

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await getMyOrders();
        setOrders(response.data);
      } catch (err) {
        console.error('Erreur lors de la récupération des commandes', err);
        showNotification('Impossible de récupérer l\'historique de vos commandes.', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [showNotification]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'En attente':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'En cours de traitement':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Expédiée':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Délivrée':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Mes Commandes</h2>
        <p className="text-slate-500 text-sm mt-1">Consultez l'historique et le statut de vos commandes passées.</p>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <p className="text-slate-500 text-sm mb-4">Vous n'avez pas encore passé de commande.</p>
          <a href="/" className="inline-block bg-indigo-600 hover:bg-indigo-750 text-white font-medium text-xs px-4 py-2 rounded-lg transition-colors shadow-sm shadow-indigo-600/10">
            Commencer mes achats
          </a>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order._id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all">
              {/* Entête de carte de commande */}
              <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex flex-wrap justify-between items-center gap-3 text-xs md:text-sm">
                <div className="flex gap-4">
                  <div>
                    <span className="text-slate-400 font-semibold uppercase tracking-wider block text-[10px]">Commande passée le</span>
                    <span className="font-semibold text-slate-800">{new Date(order.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-semibold uppercase tracking-wider block text-[10px]">Total</span>
                    <span className="font-bold text-indigo-650">{order.total.toFixed(2)} €</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-semibold uppercase tracking-wider block text-[10px]">N° de commande</span>
                    <span className="font-mono text-slate-600">{order._id}</span>
                  </div>
                </div>
                <div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${getStatusBadgeClass(order.status)}`}>
                    {order.status}
                  </span>
                </div>
              </div>

              {/* Corps de carte de commande */}
              <div className="p-6 space-y-4">
                <div className="divide-y divide-slate-100">
                  {order.items.map((item, index) => {
                    const product = item.productId;
                    const productName = product && typeof product === 'object' ? product.name : 'Produit inconnu ou retiré';

                    return (
                      <div key={index} className="flex justify-between items-center py-3 text-sm first:pt-0 last:pb-0">
                        <div>
                          <h4 className="font-semibold text-slate-800">{productName}</h4>
                          <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                            <span>Quantité : <strong className="text-slate-650">{item.quantity}</strong></span>
                          </div>
                        </div>
                        <div className="text-right font-medium text-slate-800">
                          {item.price.toFixed(2)} €
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Détails de livraison et paiement */}
                <div className="pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-600">
                  <div className="bg-slate-50/50 p-3 rounded-lg border border-slate-100">
                    <strong className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Adresse de livraison</strong>
                    <p className="font-semibold text-slate-850">{order.shippingAddress.street}</p>
                    <p>{order.shippingAddress.postalCode} {order.shippingAddress.city}</p>
                    <p className="uppercase text-[10px] text-slate-400 mt-0.5 font-bold">{order.shippingAddress.country}</p>
                  </div>
                  <div className="flex flex-col justify-center space-y-2 pl-2">
                    <p>Mode de livraison : <strong className="text-slate-800 capitalize">{order.shippingMethod}</strong></p>
                    <p>Méthode de paiement : <strong className="text-slate-800">{order.paymentMethod}</strong></p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyOrders;
